// One-time recovery codes (two-factor authentication), shown once: copy, download as .txt, and a
// confirmation that they were stored before the user may continue.
import { useId, useState, useSyncExternalStore } from 'react';
import { copyText, saveFile } from '../export';
import { formatDateTime } from '../lib/format';
import { onSessionUser } from '../lib/session';
import { Icon } from './Icon';
import { toast } from './Toasts';

export const RECOVERY_FILE_NAME = 'BAAM-Transkripcija-obnovitvene-kode.txt';

function codesText(codes: readonly string[], username?: string): string {
  return [
    'BAAM Transkripcija – obnovitvene kode za dvostopenjsko prijavo',
    username ? `Uporabnik: ${username}` : null,
    `Ustvarjeno: ${formatDateTime(new Date().toISOString())}`,
    '',
    'Vsako kodo lahko uporabite le enkrat – namesto kode iz aplikacije za preverjanje,',
    'če izgubite telefon. Hranite jih na varnem mestu (ne na istem telefonu).',
    '',
    ...codes,
    '',
  ]
    .filter((line) => line !== null)
    .join('\n');
}

interface RecoveryCodesViewProps {
  codes: readonly string[];
  username?: string;
  onDone: () => void;
  doneLabel?: string;
  /** Shown as its own page (forced 2FA setup) rather than inside a dialog. */
  page?: boolean;
}

export function RecoveryCodesView({ codes, username, onDone, doneLabel = 'Nadaljuj', page }: RecoveryCodesViewProps) {
  const [saved, setSaved] = useState(false);
  const checkId = useId();
  const titleId = useId();

  const copy = async () => {
    const ok = await copyText(codes.join('\n'));
    toast(ok ? 'Kode so kopirane v odložišče.' : 'Brskalnik ni dovolil kopiranja v odložišče.', { kind: ok ? 'success' : 'warning' });
  };
  const download = async () => {
    try {
      await saveFile(new Blob([codesText(codes, username)], { type: 'text/plain;charset=utf-8' }), RECOVERY_FILE_NAME);
    } catch {
      toast('Datoteke ni bilo mogoče shraniti. Kode kopirajte ali prepišite.', { kind: 'error' });
    }
  };

  const body = (
    <>
      {page && (
        <>
          <span className="login__icon" aria-hidden="true">
            <Icon name="lock" size={28} />
          </span>
          <h1 id={titleId} className="login__title">
            Obnovitvene kode
          </h1>
        </>
      )}
      <p className={page ? 'muted' : undefined}>
        Dvostopenjska prijava je vklopljena. Če izgubite telefon, se lahko prijavite z eno od teh kod namesto kode iz aplikacije – vsako kodo
        le enkrat. <strong>Prikazane so samo zdaj.</strong>
      </p>
      <ol className="recovery-codes" aria-label="Obnovitvene kode">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <div className="toolbar toolbar--center">
        <button type="button" className="btn btn--secondary" onClick={() => void copy()}>
          <Icon name="copy" size={20} /> Kopiraj
        </button>
        <button type="button" className="btn btn--secondary" onClick={() => void download()}>
          <Icon name="download" size={20} /> Prenesi .txt
        </button>
      </div>
      <p className="muted small">Shranite jih v upravitelja gesel ali natisnite in shranite na varno mesto – ne na isti telefon.</p>
      <label className="checkline" htmlFor={checkId}>
        <input id={checkId} type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>Shranil(-a) sem kode na varno mesto.</span>
      </label>
      <button type="button" className="btn btn--primary btn--block" disabled={!saved} onClick={onDone}>
        <Icon name="check" size={20} /> {doneLabel}
      </button>
    </>
  );

  if (!page) return <div className="stack">{body}</div>;
  return (
    <div className="page page--center page--login">
      <section className="card login" aria-labelledby={titleId}>
        {body}
      </section>
    </div>
  );
}

// ---------- codes waiting to be acknowledged (forced 2FA setup) ----------
//
// Enabling 2FA ends the session's restriction, so the app would show its pages right away; the
// codes stay on screen (App renders them first) until the user confirms they were stored. They
// belong to that user only: shown while that user is signed in, and forgotten as soon as the
// session ends or changes hands (logout here or in another tab, an ended session, another sign-in).

interface PendingCodes {
  codes: string[];
  username?: string;
  userId: string;
}

let pending: PendingCodes | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

export function showRecoveryCodes(codes: string[], username: string | undefined, userId: string): void {
  pending = { codes, username, userId };
  emit();
}

export function dismissRecoveryCodes(): void {
  if (pending === null) return;
  pending = null;
  emit();
}

onSessionUser((userId) => {
  if (pending && pending.userId !== userId) dismissRecoveryCodes();
});

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = () => pending;

/** The codes waiting for `userId` to confirm them (null for anyone else, or none). */
export function usePendingRecoveryCodes(userId: string | null): { codes: string[]; username?: string } | null {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  return current && userId !== null && current.userId === userId ? current : null;
}
